import type { DisplayCue } from "@expert/contentful/src/types";

import { useDetectedCuesStore } from "@expert/sales-tools";
import { Button, Chip, Group } from "@mantine/core";
import { type JSX, useState } from "react";

import { useAnalytics } from "@/analytics";
import { DeviceCheckMark } from "@/sales-tools/detected-cues/Icons/DeviceCheckMark";
import { useSessionId } from "@/sdk";

import classes from "../DetectedCues.module.css";

const defaultItemLimit = 4;
export function ChipRow({ chips }: { chips: DisplayCue[] }): JSX.Element | null {
    const { dispatcher } = useAnalytics();
    const [itemLimit, setItemLimit] = useState<number | undefined>(defaultItemLimit);
    const sessionId = useSessionId();

    const handleDeviceClicked = (insight: DisplayCue) => {
        void dispatcher.dispatchBusinessEvent("Device", {
            device: insight.name,
            action: insight.detected ? "remove" : "add",
        });

        if (insight.detected) {
            useDetectedCuesStore.getState().deselectDetectedCue(sessionId, insight.name);
        } else {
            useDetectedCuesStore.getState().processNewlyDetectedCue(sessionId, [
                {
                    name: insight.name,
                    count: 1,
                    type: insight.type,
                },
            ]);
        }
    };

    return (
        <Chip.Group value={chips.filter((d) => d.detected).map((device) => device.name)} multiple>
            <Group gap={5}>
                {chips.slice(0, itemLimit).map((entry) => (
                    <Chip
                        data-testid={`device-chip-${entry.name}`}
                        id={`device-chip-${entry.name}`}
                        onClick={() => handleDeviceClicked(entry)}
                        icon={
                            entry.isDetectedOnPreviousCall ? (
                                <DeviceCheckMark
                                    data-testid={`device-check-mark-icon-${entry.name}`}
                                    fill="var(--mantine-color-dark-1)"
                                />
                            ) : (
                                <DeviceCheckMark
                                    data-testid={`device-check-mark-icon-${entry.name}`}
                                    fill="var(--mantine-color-grey-9)"
                                />
                            )
                        }
                        variant="transparent"
                        size="xs"
                        key={entry.name}
                        value={entry.name}
                        classNames={{ label: classes.chip }}
                        disabled={entry.isDetectedOnPreviousCall}
                    >
                        {entry.name}
                    </Chip>
                ))}
                <Button
                    data-testid="show-more-device-chips"
                    styles={{
                        root: {
                            paddingLeft: 6,
                            paddingRight: 6,
                            height: 22,
                        },
                    }}
                    variant="filled"
                    color="dark.4"
                    className={itemLimit ? classes.chipButton : classes.showLessButton}
                    bg="dark.5"
                    onClick={() => {
                        void dispatcher.dispatchBusinessEvent("ShowMoreDeviceChipsClicked");
                        setItemLimit(itemLimit === defaultItemLimit ? undefined : defaultItemLimit);
                    }}
                >
                    {itemLimit ? "+" : "^"}
                </Button>
            </Group>
        </Chip.Group>
    );
}
